import { Day } from "src/app/core/day.model";
import { Site } from "src/app/core/sites";
import {
  ColumnFilter,
  TableColumnFilterType,
} from "src/app/core/table/table-column-filter.model";
import { UUID } from "src/utils/codecs";
import { formatDateTime } from "src/utils/converters";
import { getDurationInHighestUnit } from "src/utils/set-filters-on-resource";
import { throwUnhandledCaseError } from "./miscellaneous";

export interface SerializedFilters {
  [key: string]: unknown;
}

export function serializeColumnFilters(
  columnFilters: readonly ColumnFilter[],
  timeZone?: Site["timeZone"],
): SerializedFilters {
  const result: SerializedFilters = {};

  for (const columnFilter of columnFilters) {
    let filterValue: unknown;

    switch (columnFilter.type) {
      case TableColumnFilterType.DurationRange: {
        const min = columnFilter.value.min;
        const max = columnFilter.value.max;
        if (min || max) {
          filterValue = {
            min: min ? getDurationInHighestUnit(min) : null,
            max: max ? getDurationInHighestUnit(max) : null,
          };
        }
        break;
      }
      case TableColumnFilterType.NumberRange: {
        const min = columnFilter.value.min ?? null;
        const max = columnFilter.value.max ?? null;
        if (min || max) {
          filterValue = { min, max };
        }
        break;
      }
      case TableColumnFilterType.Days: {
        const weekdaysApiValue = columnFilter.value.serialize();
        if (weekdaysApiValue) {
          filterValue = weekdaysApiValue.split(",");
        }
        break;
      }
      case TableColumnFilterType.MultiSelect: {
        const selectedValues = columnFilter.value;
        if (selectedValues.length > 0) {
          filterValue = selectedValues.map((value) =>
            value instanceof UUID ? value.toString() : value,
          );
        }
        break;
      }
      case TableColumnFilterType.DateRange: {
        const { start, end } = columnFilter.value;
        if (!timeZone) {
          throw new Error("Time zone is required for date range filters");
        }
        filterValue = {
          min: formatDateTime(start.toDateTime(Day.startOfDay, timeZone)),
          max: formatDateTime(end.toDateTime(Day.startOfDay, timeZone)),
        };
        break;
      }
      case TableColumnFilterType.DayRange: {
        const { start, end } = columnFilter.value;
        filterValue = { min: start, max: end };
        break;
      }
      case TableColumnFilterType.Time: {
        filterValue = columnFilter.value.serialize();
        break;
      }
      case TableColumnFilterType.NumberIdentifier:
      case TableColumnFilterType.Boolean:
      case TableColumnFilterType.Number:
      case TableColumnFilterType.Text:
      case TableColumnFilterType.Dropdown: {
        filterValue = columnFilter.value;
        break;
      }
      default:
        throwUnhandledCaseError("column filter type", columnFilter);
    }
    if (filterValue !== undefined) {
      result[columnFilter.key] = filterValue;
    }
  }

  return result;
}
